import React, { useState } from 'react';
import MainLayout from '../components/MainLayout';
import { ChevronDown, ChevronUp } from 'lucide-react';
import '../styles/global.css';

const HelpPage: React.FC = () => {
  const [sections, setSections] = useState({
    announcement: false,
    distribution: false,
    roles: false,
  });

  const toggleSection = (section: keyof typeof sections) => {
    setSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <MainLayout>
      <div className="p-8 max-w-4xl mx-auto space-y-10 text-gray-800">
        <h1 className="text-3xl font-bold text-center mb-4">📘 Help & Instructions</h1>

        {/* Section: Announcement */}
        <section className="bg-white rounded-2xl shadow-md p-6 border border-gray-200">
          <h2
            className="text-2xl font-semibold mb-2 flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('announcement')}
          >
            <span className="flex items-center">
              📣 <span className="ml-2">Make Announcement</span>
            </span>
            {sections.announcement ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </h2>

          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              sections.announcement ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <ul className="list-disc list-inside space-y-1">
              <li>
                You can prepare an announcement message by filling in the form fields.
                Fields marked with <span className="text-red-500 font-bold">*</span> are required — you will not be able to send a message without completing them.
              </li>
              <li>
                Before sending the mail, click <strong>"Show Preview"</strong> to review your message.
                Please ensure all required fields are filled to enable the preview.
              </li>
            </ul>
          </div>
        </section>

        {/* Section: Distribution */}
        <section className="bg-white rounded-2xl shadow-md p-6 border border-gray-200">
          <h2
            className="text-2xl font-semibold mb-2 flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('distribution')}
          >
            <span className="flex items-center">
              📧 <span className="ml-2">Distribution List</span>
            </span>
            {sections.distribution ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </h2>
          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              sections.distribution ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <p className="mb-2">Manage your selected distribution list with ease:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>➕ Add new email addresses</li>
              <li>✏️ Edit existing entries</li>
              <li>🗑️ Remove emails from the list</li>
              <li>📁 Export the entire list as a downloadable file</li>
              <li>📥 <strong>Import from CSV</strong> (Editor role only) - Sync your distribution list with a CSV file</li>
            </ul>
            
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-300">
              <h3 className="font-semibold mb-2">📋 CSV Import Instructions:</h3>
              <p className="mb-2">When importing a CSV file:</p>
              <ul className="list-disc list-inside space-y-1 mb-3">
                <li>Emails in the CSV but <strong>not in the list</strong> → will be <span className="text-green-600 font-semibold">added</span></li>
                <li>Emails in the list but <strong>not in the CSV</strong> → will be <span className="text-red-600 font-semibold">removed</span></li>
                <li>Emails in both → remain unchanged</li>
              </ul>
              
              <h4 className="font-semibold mb-1">CSV Format Example:</h4>
              <pre className="bg-white p-3 rounded border border-gray-200 text-sm overflow-x-auto">
{`Distribution List: CEN
email1@cariad.technology
email2@cariad.technology
email3@cariad.technology
email4@cariad.technology`}
              </pre>
              <p className="text-sm text-gray-600 mt-2">
                <em>Note: The CSV file should contain one email address per line. First header row is automatically skipped if it does not contain an email address tag (@).</em>
              </p>
            </div>
          </div>
        </section>

        {/* Section: Roles */}
        <section className="bg-white rounded-2xl shadow-md p-6 border border-gray-200">
          <h2
            className="text-2xl font-semibold mb-2 flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('roles')}
          >
            <span className="flex items-center">
              🛡️ <span className="ml-2">User Roles</span>
            </span>
            {sections.roles ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </h2>
          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              sections.roles ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <p className="mb-2">There are two main roles within the application:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong>Editor</strong> – 👀 Can view and <strong>modify</strong> the distribution list.
              </li>
              <li>
                <strong>Viewer</strong> – 🔒 Can only <strong>view</strong> the list and send emails.
              </li>
            </ul>
          </div>
        </section>
      </div>
    </MainLayout>
  );
};

export default HelpPage;